import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { 
  Zap, LayoutDashboard, Eye, Megaphone, BarChart3, 
  Wand2, Settings, LogOut, Menu, X, ChevronDown, Gift, Crown, Trophy, FileText, Users, CreditCard, Video, Clock, Bell, ExternalLink, CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { creditsAPI, announcementsAPI } from '../lib/api';

const DashboardLayout = ({ isAdmin = false }) => {
  const { user, logout, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [claimingBonus, setClaimingBonus] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await announcementsAPI.getFeed();
      setAnnouncements(res.data.announcements || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      fetchAnnouncements();
      const interval = setInterval(fetchAnnouncements, 60000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, fetchAnnouncements]);

  const handleMarkRead = async (id) => {
    try {
      await announcementsAPI.markRead(id);
      setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, is_read: true } : a));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await announcementsAPI.markAllRead();
      setAnnouncements(prev => prev.map(a => ({ ...a, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const navItems = isAdmin ? [
    { path: '/admin', label: 'Admin Panel', icon: Settings }
  ] : [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/dashboard/feed', label: 'Earn Credits', icon: Eye },
    { path: '/dashboard/promotions', label: 'My Promotions', icon: Megaphone },
    { path: '/dashboard/landing-pages', label: 'Capture Pages', icon: FileText },
    { path: '/dashboard/leads', label: 'Leads', icon: Users },
    { path: '/dashboard/scheduled-messages', label: 'Scheduled', icon: Clock },
    { path: '/dashboard/video-ads', label: 'Video Ads', icon: Video },
    { path: '/dashboard/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/dashboard/leaderboard', label: 'Leaderboard', icon: Trophy },
    { path: '/dashboard/ai-tools', label: 'AI Tools', icon: Wand2 },
    { path: '/dashboard/billing', label: 'Billing', icon: CreditCard },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleClaimBonus = async () => {
    if (user?.daily_login_claimed) {
      toast.info('Daily bonus already claimed!');
      return;
    }
    
    setClaimingBonus(true);
    try {
      const response = await creditsAPI.claimDailyBonus();
      updateUser({ credits: response.data.credits, daily_login_claimed: true });
      toast.success(`+${response.data.bonus} credits! Daily bonus claimed!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim bonus');
    } finally {
      setClaimingBonus(false);
    }
  };

  const getPlanBadge = () => {
    if (user?.plan === 'power') return { label: 'POWER', color: 'bg-neon-violet text-white' };
    if (user?.plan === 'pro') return { label: 'PRO', color: 'bg-neon-mint text-black' };
    return { label: 'FREE', color: 'bg-white/10 text-gray-400' };
  };

  const planBadge = getPlanBadge();

  return (
    <div className="min-h-screen bg-void flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex w-64 flex-col bg-void-paper border-r border-white/10">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neon-mint rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-black" />
            </div>
            <div>
              <span className="font-heading font-bold text-xl text-white">TrafficFlow</span>
              <span className="font-heading font-bold text-xl text-neon-violet ml-1">AI</span>
            </div>
          </Link>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive 
                    ? 'bg-neon-mint/10 text-neon-mint' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        {/* Daily Bonus */}
        {!isAdmin && (
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleClaimBonus}
              disabled={claimingBonus || user?.daily_login_claimed}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                user?.daily_login_claimed 
                  ? 'bg-white/5 text-gray-500 cursor-not-allowed' 
                  : 'bg-neon-amber/10 text-neon-amber hover:bg-neon-amber/20'
              }`}
              data-testid="claim-daily-bonus"
            >
              <Gift className="w-5 h-5" />
              <span className="font-medium">
                {user?.daily_login_claimed ? 'Bonus Claimed' : 'Claim Daily Bonus'}
              </span>
            </button>
          </div>
        )}
        
        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-neon-mint to-neon-violet flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${planBadge.color}`}>
                {user?.plan === 'power' && <Crown className="w-3 h-3" />}
                {planBadge.label}
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-64 bg-void-paper border-r border-white/10 overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-neon-mint rounded-lg flex items-center justify-center">
                  <Zap className="w-6 h-6 text-black" />
                </div>
                <span className="font-heading font-bold text-xl text-white">TrafficFlow</span>
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <nav className="p-4 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-neon-mint/10 text-neon-mint' 
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="h-16 bg-void-paper border-b border-white/10 flex items-center justify-between px-4 lg:px-8">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex-1 lg:flex-none" />
          
          <div className="flex items-center gap-4">
            {/* Credits Display */}
            {!isAdmin && (
              <div className="flex items-center gap-2 px-4 py-2 bg-void rounded-lg border border-white/10">
                <Zap className="w-4 h-4 text-neon-mint" />
                <span className="font-mono font-bold text-white" data-testid="credit-balance">
                  {user?.credits || 0}
                </span>
                <span className="text-gray-500 text-sm">credits</span>
              </div>
            )}

            {/* Notifications Bell */}
            {!isAdmin && (
              <Popover open={notifOpen} onOpenChange={setNotifOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="relative p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                    data-testid="notifications-bell"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white" data-testid="unread-count">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0 bg-void-paper border-white/10">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <h3 className="text-white font-semibold text-sm">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-neon-mint hover:underline flex items-center gap-1"
                        data-testid="mark-all-read-btn"
                      >
                        <CheckCheck className="w-3 h-3" />
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {announcements.length === 0 ? (
                      <div className="px-4 py-8 text-center text-gray-500 text-sm">
                        No notifications yet
                      </div>
                    ) : (
                      announcements.slice(0, 20).map((a) => (
                        <div
                          key={a.id}
                          className={`px-4 py-3 border-b border-white/5 cursor-pointer transition-colors hover:bg-white/5 ${!a.is_read ? 'bg-neon-mint/5' : ''}`}
                          onClick={() => {
                            if (!a.is_read) handleMarkRead(a.id);
                            if (a.link) window.open(a.link, '_blank');
                          }}
                          data-testid={`notification-item-${a.id}`}
                        >
                          <div className="flex items-start gap-3">
                            {!a.is_read && (
                              <span className="mt-1.5 w-2 h-2 rounded-full bg-neon-mint flex-shrink-0" />
                            )}
                            <div className={`flex-1 min-w-0 ${a.is_read ? 'pl-5' : ''}`}>
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-medium truncate ${a.priority === 'urgent' ? 'text-red-400' : a.priority === 'important' ? 'text-neon-amber' : 'text-white'}`}>
                                  {a.title}
                                </p>
                                {a.link && <ExternalLink className="w-3 h-3 text-gray-500 flex-shrink-0" />}
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{a.message}</p>
                              <p className="text-[10px] text-gray-600 mt-1">
                                {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-gray-300 hover:text-white">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-neon-mint to-neon-violet flex items-center justify-center text-white font-bold text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden sm:inline">{user?.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-void-paper border-white/10">
                <div className="px-3 py-2">
                  <p className="text-white font-medium">{user?.name}</p>
                  <p className="text-gray-500 text-sm">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                {user?.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer text-gray-300 hover:text-white focus:text-white">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin Panel
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer text-gray-300 hover:text-white focus:text-white"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
